import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { ltpTag } from "@/lib/priceLabel";
import AppShell from "@/components/AppShell";
import PageHeader from "@/components/PageHeader";
import PriceAlerts from "@/pages/PriceAlerts";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Plus, Trash2, TrendingUp, TrendingDown, Star, Radio, Loader2, Upload } from "lucide-react";

const fmt = (n, d = 2) =>
  n == null || isNaN(n) ? "—" : Number(n).toLocaleString("en-IN", { minimumFractionDigits: d, maximumFractionDigits: d });

const changeCls = (v) => (v > 0 ? "txt-profit" : v < 0 ? "txt-loss" : "txt-muted");

export default function Watchlist() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get("tab") || "watchlist";
  const setTab = (t) => setSearchParams(t === "watchlist" ? {} : { tab: t });

  const [items, setItems] = useState([]);
  const [newSymbol, setNewSymbol] = useState("");
  const [adding, setAdding] = useState(false);
  const [loading, setLoading] = useState(true);

  // Bulk paste state
  const [bulkText, setBulkText] = useState("");
  const [bulkInSignals, setBulkInSignals] = useState(false);
  const [bulkAdding, setBulkAdding] = useState(false);
  const [showBulk, setShowBulk] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/watchlist");
      setItems(data.items || []);
    } catch {
      toast.error("Failed to load watchlist");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 5000);
    return () => clearInterval(id);
  }, [load]);

  const addSymbol = async () => {
    const sym = newSymbol.trim().toUpperCase();
    if (!sym) return;
    setAdding(true);
    try {
      await api.post("/watchlist", { symbol: sym });
      setNewSymbol("");
      await load();
      toast.success(`${sym} added to watchlist`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add symbol");
    } finally {
      setAdding(false);
    }
  };

  const removeSymbol = async (symbol) => {
    try {
      await api.delete(`/watchlist/${symbol}`);
      setItems((prev) => prev.filter((i) => i.symbol !== symbol));
      toast.success(`${symbol} removed`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to remove");
    }
  };

  const toggleSignalFeed = async (symbol, currentValue) => {
    try {
      const { data } = await api.patch(`/watchlist/${symbol}/signals`);
      setItems((prev) =>
        prev.map((i) => i.symbol === symbol ? { ...i, include_in_signals: data.include_in_signals } : i)
      );
      toast.success(
        data.include_in_signals
          ? `${symbol} added to Signal Feed`
          : `${symbol} removed from Signal Feed`
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update");
    }
  };

  const bulkAdd = async () => {
    const symbols = bulkText
      .split(/[\n,\s]+/)
      .map((s) => s.trim().toUpperCase())
      .filter(Boolean);
    if (symbols.length === 0) {
      toast.error("No valid symbols found");
      return;
    }
    setBulkAdding(true);
    try {
      const { data } = await api.post("/watchlist/bulk", {
        symbols,
        include_in_signals: bulkInSignals,
      });
      await load();
      setBulkText("");
      setShowBulk(false);
      const msg = `Added ${data.added_count} symbol${data.added_count !== 1 ? "s" : ""}` +
        (data.skipped_count > 0 ? ` · ${data.skipped_count} skipped (duplicates/limit)` : "");
      toast.success(msg);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk add failed");
    } finally {
      setBulkAdding(false);
    }
  };

  // PriceAlerts is rendered standalone — wrap it without its own AppShell
  if (tab === "alerts") {
    return (
      <AppShell>
        <PageHeader overline="Portfolio" title="Watchlist & Alerts" />
        <div className="border-b border-[var(--border)] px-6 flex gap-1">
          {["watchlist", "alerts"].map((t) => (
            <button key={t} onClick={() => setTab(t)}
              className={`px-4 py-2.5 text-xs font-section tracking-wider border-b-2 transition-colors ${t === tab ? "border-[var(--accent)] txt-accent" : "border-transparent txt-muted hover:text-[var(--text-primary)]"}`}>
              {t === "watchlist" ? "Watchlist" : "Price Alerts"}
            </button>
          ))}
        </div>
        <PriceAlerts embedded />
      </AppShell>
    );
  }

  return (
    <AppShell>
      <PageHeader overline="Portfolio" title="Watchlist & Alerts" />
      <div className="border-b border-[var(--border)] px-6 flex gap-1">
        {["watchlist", "alerts"].map((t) => (
          <button key={t} onClick={() => setTab(t)}
            className={`px-4 py-2.5 text-xs font-section tracking-wider border-b-2 transition-colors ${t === tab ? "border-[var(--accent)] txt-accent" : "border-transparent txt-muted hover:text-[var(--text-primary)]"}`}>
            {t === "watchlist" ? "Watchlist" : "Price Alerts"}
          </button>
        ))}
      </div>
      <div className="p-4 sm:p-6 lg:p-8 space-y-4 sm:space-y-6">
        {/* Add symbol row */}
        <div className="panel p-5 space-y-4">
          <div className="overline mb-3">Add symbol</div>
          <div className="flex gap-3 max-w-sm">
            <Input
              value={newSymbol}
              onChange={(e) => setNewSymbol(e.target.value.toUpperCase())}
              onKeyDown={(e) => e.key === "Enter" && addSymbol()}
              placeholder="e.g. RELIANCE, NIFTY, GOLD"
              className="rounded-none bg-[var(--bg-page)] border-[var(--border)] font-mono-data uppercase"
            />
            <Button
              onClick={addSymbol}
              disabled={adding || !newSymbol.trim()}
              className="rounded-none bg-white text-black hover:bg-zinc-200 font-section tracking-wider"
            >
              <Plus className="w-4 h-4 mr-1" /> ADD
            </Button>
            <Button
              variant="outline"
              onClick={() => setShowBulk((v) => !v)}
              className="rounded-none border-[var(--border)] txt-secondary hover:text-[var(--text-primary)] font-section"
              title="Bulk import"
            >
              <Upload className="w-4 h-4 mr-1" /> Bulk
            </Button>
          </div>

          {/* Bulk paste panel */}
          {showBulk && (
            <div className="border border-[var(--border)] bg-[var(--bg-page)] p-4 space-y-3 max-w-lg">
              <div className="text-xs txt-secondary font-section tracking-wider">BULK IMPORT — paste symbols (comma, space, or newline separated)</div>
              <textarea
                value={bulkText}
                onChange={(e) => setBulkText(e.target.value.toUpperCase())}
                placeholder={"RELIANCE\nTCS\nINFY\nor: SBIN, HDFC, ICICI"}
                rows={5}
                className="w-full bg-[var(--bg-surface)] border border-[var(--border)] rounded-none p-3 text-sm font-mono-data text-[var(--text-primary)] resize-y focus:outline-none focus:border-[var(--accent)]"
              />
              <div className="flex items-center justify-between gap-4">
                <label className="flex items-center gap-2 cursor-pointer select-none">
                  <div
                    onClick={() => setBulkInSignals((v) => !v)}
                    className={`w-9 h-5 rounded-full relative transition-colors cursor-pointer ${
                      bulkInSignals ? "bg-[var(--accent)]" : "bg-[var(--bg-surface-2)]"
                    }`}
                  >
                    <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${bulkInSignals ? "translate-x-4" : "translate-x-0.5"}`} />
                  </div>
                  <span className="text-xs txt-secondary flex items-center gap-1">
                    <Radio className="w-3 h-3" /> Include in Signal Feed
                  </span>
                </label>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => { setShowBulk(false); setBulkText(""); }}
                    className="rounded-none border-[var(--border)] txt-muted text-xs"
                  >
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    onClick={bulkAdd}
                    disabled={bulkAdding || !bulkText.trim()}
                    className="rounded-none bg-[var(--accent)] hover:bg-indigo-500 text-white font-section tracking-wider text-xs"
                  >
                    {bulkAdding ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Upload className="w-3.5 h-3.5 mr-1" />}
                    IMPORT
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Watchlist table */}
        <div className="panel">
          <div className="p-4 border-b border-[var(--border)] flex items-center justify-between">
            <div className="overline">
              {items.length} / 50 symbols
            </div>
            <div className="flex items-center gap-2">
              {items.filter((i) => i.include_in_signals).length > 0 && (
                <span className="text-[10px] font-section txt-accent border border-[var(--accent)]/30 bg-[var(--accent)]/10 px-1.5 py-0.5 rounded">
                  {items.filter((i) => i.include_in_signals).length} in signal feed
                </span>
              )}
              <Star className="w-4 h-4 txt-muted" />
            </div>
          </div>

          {loading && (
            <div className="p-8 text-center txt-muted text-sm">Loading…</div>
          )}

          {!loading && items.length === 0 && (
            <div className="p-8 text-center">
              <Star className="w-8 h-8 txt-muted mx-auto mb-3" />
              <div className="txt-muted text-sm">No symbols yet. Add your first symbol above.</div>
            </div>
          )}

          {items.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[var(--border)]">
                    {["Symbol", "LTP", "Change %", "Volume", "Signal Feed", ""].map((h) => (
                      <th key={h} className={`overline px-4 py-3 text-left ${h === "" ? "w-10" : ""}`}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => (
                    <tr
                      key={item.symbol}
                      className="border-b border-[var(--border)] hover:bg-[var(--bg-surface-2)] transition-colors"
                    >
                      <td className="px-4 py-3 font-section font-bold">{item.symbol}</td>
                      <td className="px-4 py-3 font-mono-data">
                        {item.ltp ? `₹${fmt(item.ltp)}` : <span className="txt-muted text-xs">No price</span>}
                        {item.ltp && ltpTag(item) ? (
                          <span className="ml-1 text-[9px] txt-muted font-section tracking-wider"
                                title={item.ltp_source ? `source: ${item.ltp_source}` : undefined}
                                data-testid={`watchlist-ltp-label-${item.symbol}`}>
                            {ltpTag(item)}
                          </span>
                        ) : null}
                      </td>
                      <td className={`px-4 py-3 font-mono-data text-sm ${changeCls(item.change_pct)}`}>
                        {item.change_pct != null ? (
                          <span className="flex items-center gap-1">
                            {item.change_pct >= 0
                              ? <TrendingUp className="w-3.5 h-3.5" />
                              : <TrendingDown className="w-3.5 h-3.5" />}
                            {item.change_pct >= 0 ? "+" : ""}{fmt(item.change_pct)}%
                          </span>
                        ) : "—"}
                      </td>
                      <td className="px-4 py-3 font-mono-data txt-muted text-xs">
                        {item.volume ? fmt(item.volume, 0) : "—"}
                      </td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => toggleSignalFeed(item.symbol, item.include_in_signals)}
                          title={item.include_in_signals ? "Remove from Signal Feed" : "Include in Signal Feed"}
                          className={`flex items-center gap-1.5 text-[10px] font-section px-2 py-1 rounded border transition-colors ${
                            item.include_in_signals
                              ? "bg-[var(--accent)]/15 border-[var(--accent)]/40 txt-accent hover:bg-[var(--accent)]/25"
                              : "bg-transparent border-[var(--border)] txt-muted hover:border-[var(--accent)]/40 hover:txt-accent"
                          }`}
                        >
                          <Radio className="w-3 h-3" />
                          {item.include_in_signals ? "In Feed" : "Add to Feed"}
                        </button>
                      </td>
                      <td className="px-4 py-3">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => removeSymbol(item.symbol)}
                          className="rounded-none h-7 w-7 p-0 text-zinc-500 hover:text-red-400 hover:bg-red-500/10"
                          title={`Remove ${item.symbol}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </AppShell>
  );
}
